import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    listRentalWeekdayPrices,
    upsertRentalWeekdayPrice,
    deleteRentalWeekdayPrice,
} from "../rentalService";
import { CollapsibleBlock } from "../builder/pricingCompactUi";

const DOW = [
    { id: 0, label: "Sunday" },
    { id: 1, label: "Monday" },
    { id: 2, label: "Tuesday" },
    { id: 3, label: "Wednesday" },
    { id: 4, label: "Thursday" },
    { id: 5, label: "Friday" },
    { id: 6, label: "Saturday" },
];

const moneyInput = "w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm";

const RentalWeekdayPricing = ({ rentalId, regularPricePerHour }) => {
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);

    const { data: res, isLoading } = useQuery({
        queryKey: ["rentalWeekdayPrices", rentalId],
        queryFn: () => listRentalWeekdayPrices(rentalId),
        enabled: !!rentalId,
        staleTime: 60_000,
    });

    const rows = Array.isArray(res?.data) ? res.data : [];
    const byDow = useMemo(() => {
        const m = new Map();
        rows.forEach((r) => m.set(Number(r.day_of_week), r));
        return m;
    }, [rows]);

    const [draftByDow, setDraftByDow] = useState(() => {
        const m = new Map();
        DOW.forEach((d) => m.set(d.id, { id: null, value: "" }));
        return m;
    });

    // Keep UI in sync with server response
    useEffect(() => {
        const next = new Map();
        DOW.forEach((d) => {
            const row = byDow.get(d.id);
            next.set(d.id, {
                id: row?.id ?? null,
                value: row?.price_per_hour != null && row?.price_per_hour !== "" ? String(row.price_per_hour) : "",
            });
        });
        setDraftByDow(next);
    }, [byDow]);

    const invalidate = () => {
        queryClient.invalidateQueries(["rentalWeekdayPrices", rentalId]);
        queryClient.invalidateQueries(["rentalItem", rentalId]);
    };

    const saveMut = useMutation({
        mutationFn: ({ day_of_week, price_per_hour }) =>
            upsertRentalWeekdayPrice(rentalId, { day_of_week, price_per_hour }),
        onSuccess: (res, vars) => {
            // Optimistically set returned row id if available
            const wp = res?.weekday_price;
            setDraftByDow((prev) => {
                const next = new Map(prev);
                const existing = next.get(vars.day_of_week) || { id: null, value: "" };
                next.set(vars.day_of_week, {
                    id: wp?.id ?? existing.id,
                    value: String(vars.price_per_hour),
                });
                return next;
            });
            invalidate();
        },
    });

    const delMut = useMutation({
        mutationFn: (id) => deleteRentalWeekdayPrice(id),
        onSuccess: (_res, id) => {
            // Clear the matching draft row
            setDraftByDow((prev) => {
                const next = new Map(prev);
                for (const [dow, v] of next.entries()) {
                    if (String(v?.id) === String(id)) {
                        next.set(dow, { id: null, value: "" });
                        break;
                    }
                }
                return next;
            });
            invalidate();
        },
    });

    const customizedCount = useMemo(
        () =>
            DOW.filter((d) => {
                const draft = draftByDow.get(d.id);
                return String(draft?.value ?? "").trim() !== "";
            }).length,
        [draftByDow]
    );

    const subtitle = useMemo(() => {
        if (customizedCount > 0) {
            return `${customizedCount} weekday override${customizedCount === 1 ? "" : "s"}`;
        }
        if (regularPricePerHour != null && regularPricePerHour !== "") {
            return `Uses regular ₹${Number(regularPricePerHour).toFixed(2)}/hr`;
        }
        return "Optional overrides";
    }, [customizedCount, regularPricePerHour]);

    if (!rentalId) return null;
    if (isLoading) {
        return <div className="text-sm text-gray-500 py-2">Loading weekday pricing…</div>;
    }

    return (
        <CollapsibleBlock
            title="Weekday pricing"
            subtitle={subtitle}
            open={open}
            onToggle={() => setOpen((o) => !o)}
        >
            <p className="mb-3 text-xs text-gray-500">
                Set a special hourly rate for a specific weekday. This applies indefinitely until you change it.
            </p>

            <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="text-left text-gray-500 border-b border-gray-100">
                            <th className="py-2 pr-3">Day</th>
                            <th className="py-2 pr-3">₹ / hour</th>
                            <th className="py-2 pr-3 text-gray-400">Regular</th>
                            <th className="py-2 pr-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {DOW.map((d) => {
                            const row = byDow.get(d.id);
                            const draft = draftByDow.get(d.id) || { id: row?.id ?? null, value: row?.price_per_hour ?? "" };
                            return (
                                <tr key={d.id} className="border-b border-gray-50">
                                    <td className="py-2 pr-3 font-medium text-gray-800">{d.label}</td>
                                    <td className="py-2 pr-3">
                                        <div className="relative max-w-[220px]">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                                                ₹
                                            </span>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                className={moneyInput}
                                                value={draft.value}
                                                placeholder="(uses regular)"
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setDraftByDow((prev) => {
                                                        const next = new Map(prev);
                                                        next.set(d.id, { ...draft, value: v });
                                                        return next;
                                                    });
                                                }}
                                                onBlur={(e) => {
                                                    const raw = e.target.value;
                                                    if (raw === "" || raw === null || raw === undefined) {
                                                        // Clear if exists, and clear UI immediately
                                                        if (draft?.id) delMut.mutate(draft.id);
                                                        setDraftByDow((prev) => {
                                                            const next = new Map(prev);
                                                            next.set(d.id, { id: null, value: "" });
                                                            return next;
                                                        });
                                                        return;
                                                    }
                                                    const n = Number(raw);
                                                    if (!Number.isFinite(n) || n < 0) return;
                                                    saveMut.mutate({ day_of_week: d.id, price_per_hour: n });
                                                }}
                                            />
                                        </div>
                                    </td>
                                    <td className="py-2 pr-3 text-xs text-gray-400">
                                        {regularPricePerHour != null ? `₹${Number(regularPricePerHour).toFixed(2)}` : "—"}
                                    </td>
                                    <td className="py-2 pr-3 text-right">
                                        {draft?.id ? (
                                            <button
                                                type="button"
                                                className="text-red-600 text-xs font-semibold"
                                                disabled={delMut.isPending}
                                                onClick={() => {
                                                    delMut.mutate(draft.id);
                                                    setDraftByDow((prev) => {
                                                        const next = new Map(prev);
                                                        next.set(d.id, { id: null, value: "" });
                                                        return next;
                                                    });
                                                }}
                                            >
                                                Clear
                                            </button>
                                        ) : (
                                            <span className="text-xs text-gray-300">—</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="mt-3 text-[11px] text-gray-400">
                Tip: Set weekday rate and click outside the input to save (auto-saves on blur). Leave empty to use the
                regular hourly price.
            </div>
        </CollapsibleBlock>
    );
};

export default RentalWeekdayPricing;
